import { Backdrop, Box, CircularProgress, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import { StyledTable } from "../../../shared/ReusableTable/TableStyle";
import { setImportDataDetails } from "../../../store/Financials/importDataSlice";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import { getAllFinancialYear, postFiscalExternalAccountSystemSubChartAccountsTransactions, saveFiscalExternalAccountSystem } from "../services/Financials/budget.service";
import { FiscalExternalAccountSystemVO } from "../services/Financials/financials";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../StyledComponents/Container";
import CommonUtilities from "../Utils/CommonUtilities";
import { notifyUser } from "../Utils/notifyUser";
import { Column } from "./ReusableSubChartsAccountTable";

interface FileInfo {
    name: string;
    size: number;
    type: string;
    file: File;
}
type VersionEntry = {
    id: number;
    version: number;
};


const ActualsImported = () => {
    const dispatch = useDispatch();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const userDetails = useSelector((state: RootState) => state.auth);
    const importData = useSelector((state: RootState) => state?.importData);
    const [financialYearsList, SetFinancialYearsList] = useState([]);
    const type = 4;
    const period = importData?.period ?? null;
    const month = importData?.month ?? null;
    const year = importData?.year ?? "";
    const [loadingFlag, setLoadingFlag] = useState(false);
    const rowData = importData?.rowData ?? [];
    const forecastMappingDetails = importData?.forecastMappingDetails ?? [];
    const transformedImport = importData?.transformedImport ?? [];
    const filteredMonthsList = importData?.filteredMonthsList ?? [];
    const participantId = importData?.participantId ?? 0;

    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const [updatedVersionMap, setUpdatedVersionMap] = useState<Map<string, VersionEntry>>(new Map());
 const columns: Column[] = [
        { id: "locationGL", label: translate('locationGL'), minWidth: 150, align: "left" },
        { id: "amount", label: translate('amount'), minWidth: 150, align: "right" },
        { id: "dioceseGLMapping", label: translate('dioceseGLMapping'), minWidth: 250, align: "left" },
    ];
    const handleBack = () => {
        createRequestPayload();
        navigate("/actualsImport");
    };
    const createRequestPayload = () => {
        const fiscalExternalAccountSystemVOList: FiscalExternalAccountSystemVO[] = [];
        const normalize = (str: string) => str?.toString().replace(/\s+/g, " ").trim();
        transformedImport.forEach((item: any) => {
            const mappingDetails = item.dioceseGLMapping;
            const matchedDetail = forecastMappingDetails.find(
                (detail: any) =>
                    normalize(detail.description) === normalize(item.locationGL)
            );
            const previousAccountNumber = matchedDetail?.previousAccountNumber != null ? matchedDetail.previousAccountNumber : matchedDetail?.accountNumber ?? "0";
            if (mappingDetails) {
                const newAccountNumber = mappingDetails.subChartAccountNumber;
                if (previousAccountNumber !== newAccountNumber) {
                    fiscalExternalAccountSystemVOList.push({
                        id: matchedDetail?.id ?? 0,
                        clientId: userDetails?.clientId,
                        participantId: participantId,
                        version: matchedDetail?.version ?? 0,
                        accountNumber: newAccountNumber,
                        previousAccountNumber: previousAccountNumber,
                        description: item.locationGL,
                    });
                } else if (matchedDetail?.previousAccountNumber != null && matchedDetail?.previousAccountNumber == newAccountNumber) {
                    fiscalExternalAccountSystemVOList.push({
                        id: matchedDetail?.id ?? 0,
                        clientId: userDetails?.clientId,
                        participantId: participantId,
                        version: matchedDetail?.version ?? 0,
                        accountNumber: newAccountNumber,
                        previousAccountNumber: "",
                        description: item.locationGL,
                    });
                }
                return;
            }
            if (!mappingDetails && previousAccountNumber != "" && previousAccountNumber != "0") {
                fiscalExternalAccountSystemVOList.push({
                    id: matchedDetail?.id ?? 0,
                    clientId: userDetails?.clientId,
                    participantId: participantId,
                    version: matchedDetail?.version ?? 0,
                    accountNumber: 0, // reset
                    previousAccountNumber: previousAccountNumber,
                    description: matchedDetail?.description ?? item.locationGL,
                });
                return;
            }
        });

        if (fiscalExternalAccountSystemVOList.length === 0) {
            notifyUser(constants.ERROR_VALIDATION_MESSAGE_NO_VALUE_HAVE_BEEN_MODIFIED, 'info', translate('importedActuals'));
            setLoadingFlag(false);
            return;
        }

        const request = {
            userName: userDetails.emailId,
            fiscalExternalAccountSystemVOList,
        };

        // Update forecastMappingDetails with the updated objects from request.fiscalExternalAccountSystemVOList
        const updatedForecastMappingDetails = importData.forecastMappingDetails.map((row) => {
            const updated = request.fiscalExternalAccountSystemVOList.find(
                (item) => item.description === row.description
            );
            if (updated) {
                return {
                    ...row,
                    accountNumber: updated.accountNumber,
                    previousAccountNumber: updated.previousAccountNumber,
                    id: updated.id,
                    version: updated.version,
                };
            }
            return row;
        });

        // Update Redux store with the updated forecastMappingDetails
        dispatch(setImportDataDetails({
            ...importData,
            forecastMappingDetails: updatedForecastMappingDetails
        }));
        return request;
    };

    async function handleSave() {
        setLoadingFlag(true);
        const request = createRequestPayload();
        if (!request) {
            // createRequestPayload already handles notification and loadingFlag
            return;
        }
        try {
            const responseList = await saveFiscalExternalAccountSystem(request);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(responseList?.data?.message, 'success', translate('importedActuals'));
                const versionMap = new Map<string, VersionEntry>();
                responseList.data?.data?.fiscalExternalAccountSystemVOList?.forEach((item: any) => {
                    if (item.description) {
                        versionMap.set(item.description, {
                            id: item.id,
                            version: item.version,
                        });
                    }
                });
                setUpdatedVersionMap(versionMap);
            } else {
                notifyUser(responseList?.data?.message, 'error', translate('importedActuals'));
            }
        } catch (error) {
            console.error(error);
            notifyUser(constants.ERROR_VALIDATION_MESSAGE_ERROR_OCCURRED_WHILE_POSTING_THE_EXCEL_SHEET_DETAILES, "error", translate("importedActuals"));
        } finally {
            setLoadingFlag(false);
        }
    }
    async function handleImport() {
        setLoadingFlag(true);

        try {
            const normalize = (str: string) =>
                str?.toString().replace(/\s+/g, " ").trim();

            const fiscalExternalAccountSystemMappingVOS: any[] = [];

            transformedImport.forEach((row: any) => {
                const mappingDetails = row.dioceseGLMapping;

                const matchedDetail = forecastMappingDetails?.find(
                    (detail: any) =>
                        normalize(detail.description) === normalize(row.locationGL)
                );

                const previousAccountNumber = matchedDetail?.previousAccountNumber != null ? matchedDetail.previousAccountNumber : matchedDetail?.accountNumber ?? "0";

                let subChartAccountNo: string | null = null;

                // ✅ CASE 1: mapping exists
                if (mappingDetails) {
                    const newAccountNumber = mappingDetails.subChartAccountNumber;

                    if (newAccountNumber !== null && newAccountNumber !== "") {
                        subChartAccountNo = newAccountNumber;
                    } else {
                        subChartAccountNo = "0";
                    }
                }

                // ✅ CASE 2: mapping removed
                else if (!mappingDetails && previousAccountNumber != "" && previousAccountNumber  != "0") {
                    subChartAccountNo = "0";
                }

                // 🔹 Amount parsing (keep as is)
                const parseAmount = (value: any): number => {
                    if (value == null) return 0;

                    let str = value.toString()
                        .replace(/[−–—]/g, '-')
                        .replace(/,/g, '')
                        .replace(/\s+/g, '');

                    if (!str) return 0;

                    let isNegative = false;
                    if (str.startsWith('(') && str.endsWith(')')) {
                        isNegative = true;
                        str = str.slice(1, -1);
                    }

                    const parsed = Number(str);
                    return isNaN(parsed) ? 0 : (isNegative ? -parsed : parsed);
                };

                let monthsList: any[] = [];

                if (row.monthList && Array.isArray(row.monthList)) {
                    monthsList = row.monthList.map((m: any) => ({
                        month: m.name,
                        amount: parseAmount(m.amouunt),
                    }));
                } else {
                    monthsList = CommonUtilities.monthOrderList
                        .filter(month => row[month] !== undefined)
                        .map(month => ({
                            month,
                            amount: parseAmount(row[month]),
                        }));
                }

                const mapEntry = updatedVersionMap.get(row.locationGL);
                if (subChartAccountNo === "0" && previousAccountNumber != "" && previousAccountNumber  != "0") {
                    fiscalExternalAccountSystemMappingVOS.push({
                        id: mapEntry?.id ?? matchedDetail?.id ?? 0,
                        version: mapEntry?.version ?? matchedDetail?.version ?? 0,
                        description: matchedDetail?.description ?? row.locationGL,
                        subChartAccountNo,
                        previousAccountNumber,
                        subChartTransForecastMonthsVOList: monthsList,
                    });

                } else if (subChartAccountNo && previousAccountNumber != "") {
                    fiscalExternalAccountSystemMappingVOS.push({
                        id: mapEntry?.id ?? matchedDetail?.id ?? 0,
                        clientId: userDetails?.clientId,
                        version: mapEntry?.version ?? matchedDetail?.version ?? 0,
                        description: matchedDetail?.description ?? row.locationGL,
                        subChartAccountNo,
                        previousAccountNumber,
                        subChartTransActualsMonthsVOList: monthsList
                    });
                }
            });

            if (fiscalExternalAccountSystemMappingVOS.length === 0) {
                notifyUser(constants.ERROR_VALIDATION_MESSAGE_NO_VALUE_HAVE_BEEN_MODIFIED, "info", translate("importedActuals"));

                setLoadingFlag(false);
                return;
            }

            const request = {
                participantId: participantId,
                financialYear: year,
                userName: userDetails.emailId,
                clientId: userDetails?.clientId,
                version: state?.version ?? 0,
                fiscalExternalAccountSystemMappingVOS
            };

            const response = await postFiscalExternalAccountSystemSubChartAccountsTransactions(request);

            if (response?.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(
                    response.data?.message ?? translate('importSuccessfully'),
                    'success',
                    translate('importedActuals')
                );
                navigate("/actualsListing");
            } else {
                notifyUser(
                    response.data?.message ?? translate('importFailed'),
                    'error',
                    translate('importedActuals')
                );
            }

        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
                notifyUser(error.response?.data?.message ?? translate('apiRequestFailed'), 'error', translate('importedActuals'));
            } else {
                console.error(translate('unexpectedError'), error);
                notifyUser(translate('anUnexpectedError'), 'error', translate('importedActuals'));
            }
        } finally {
            setLoadingFlag(false);
        }
    }
    useEffect(() => {
        if (type) {
            getParticipantForecastStatusInfo();
        }
    }, [type]);
    async function getParticipantForecastStatusInfo() {
        setIsLoading(true)
        try {
            const responseList = await getAllFinancialYear(type, userDetails?.clientId);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                SetFinancialYearsList(responseList?.data?.data?.financialYearsList);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('importedActuals')} </SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        {rowData && (
                            <TableContainer sx={{ overflow: 'auto' }}>
                                <StyledTable
                                    stickyHeader
                                    aria-label="sticky table"
                                    size="small"
                                    sx={{
                                        [`& .${tableCellClasses.root}`]: {
                                            borderBottom: 'none',
                                            borderTop: 'none',
                                        },
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((column) => (
                                                <TableCell key={column.id} align={column.align} className="cellTask">
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow>
                                                <TableCell align="center" colSpan={columns.length}>
                                                    <div className="loading-container">
                                                        <CircularProgress />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            transformedImport.map((row: any, index: number) => {
                                                if (!row.locationGL || row.locationGL.trim() === "") {
                                                    return null;
                                                }
                                                return (
                                                    <TableRow key={row.id || index} sx={{ backgroundColor: index % 2 === 0 ? "#f1f3f4" : "#fff" }}>
                                                        {columns.map((column) => (
                                                            <TableCell key={column.id} align={column.align} className="left-aligned-text">
                                                                {column.id === "dioceseGLMapping" ? (
                                                                    row.dioceseGLMapping ? row.dioceseGLMapping.displayName : "-"
                                                                ) : column.id === "locationGL" ? (
                                                                    row.locationGL ?? "-"
                                                                ) : column.id === "amount" ? (
                                                                    row.amount ?? "0.00"
                                                                ) : (
                                                                    row[column.id] ?? "-"
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            })
                                        )}


                                    </TableBody>
                                </StyledTable>
                            </TableContainer>
                        )}
                        <Box className={basestyle.buttoncontainer} sx={{ mt: 3 }}>
                            <ContainedButton onClick={handleBack}> {translate('back')}  </ContainedButton>
                            <ContainedButton onClick={handleSave}  > {translate('save')} </ContainedButton>
                            <ContainedButton onClick={handleImport}  > {translate('import')} </ContainedButton>
                        </Box>
                        <Backdrop
                            sx={{
                                color: "#fff",
                                zIndex: (theme) => theme.zIndex.drawer + 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                            open={loadingFlag}
                        >
                            <CircularProgress color="inherit" />
                            <SubSectionTitle  >{translate('pleasewait')}</SubSectionTitle>
                        </Backdrop>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>

    );
};
export default ActualsImported;